import { useId, useState } from 'react';
import { parseBlocks } from '../utils/text-blocks.js';

// Text a session wrote after reading issues from others: untrusted. It keeps the little structure plain text carries
// (numbered steps, bullets, paragraphs, `code`, **bold**) and nothing else: no Markdown library, no HTML, no links,
// React text nodes only.

/** **between double asterisks** as bold, the rest as text */
function Strong({ text }: { text: string }) {
  return text.split(/(\*\*[^*\n]+\*\*)/).map((part, i) => (part.length > 4 && part.startsWith('**') && part.endsWith('**')
    ? <strong key={i} className="font-semibold" style={{ color: 'var(--color-text)' }}>{part.slice(2, -2)}</strong>
    : part));
}

/** `between backticks` as code in the mono face, **bold** outside it, the rest as text (one line: a title) */
export function Inline({ text }: { text: string }) {
  const parts = text.split(/(`[^`\n]+`)/);
  return parts.map((part, i) => (part.length > 2 && part.startsWith('`') && part.endsWith('`')
    ? <code key={i} className="rounded px-1 py-px text-[0.92em] [overflow-wrap:anywhere]" style={{ fontFamily: 'var(--font-mono)', backgroundColor: 'var(--color-surface-3)', color: 'var(--color-text)' }}>{part.slice(1, -1)}</code>
    : <Strong key={i} text={part} />));
}

/** The structured text, unclamped */
export function TextBody({ text, className = 'text-sm', color = 'var(--color-text)' }: { text: string; className?: string; color?: string }) {
  const blocks = parseBlocks(text);
  return (
    <div className={`min-w-0 space-y-2 break-words leading-relaxed ${className}`} style={{ color }}>
      {blocks.map((b, i) => {
        if (b.kind === 'p') return <p key={i} className="whitespace-pre-line"><Inline text={b.text} /></p>;
        const items = b.items.map((it, j) => <li key={j} className="whitespace-pre-line pl-1"><Inline text={it} /></li>);
        return b.kind === 'ol'
          ? <ol key={i} start={b.start} className="list-decimal space-y-1.5 pl-6 marker:text-(--color-text-2) marker:tabular-nums">{items}</ol>
          : <ul key={i} className="list-disc space-y-1 pl-6 marker:text-(--color-text-3)">{items}</ul>;
      })}
    </div>
  );
}

/** Long enough to fold: about five lines of the reading measure */
const isLong = (text: string) => text.length > 420 || text.split('\n').length > 6;

/** Untrusted text, structured, folded when long with Show all / Show less */
export function PlainText({ text, clamp = true, className, color }: { text: string; clamp?: boolean; className?: string; color?: string }) {
  const [all, setAll] = useState(false);
  const id = useId();
  const long = clamp && isLong(text);
  const folded = long && !all;
  return (
    <div className="min-w-0">
      <div id={id} className={folded ? 'max-h-[7.75rem] overflow-hidden' : undefined}
        style={folded ? { maskImage: 'linear-gradient(to bottom, #000 65%, transparent)', WebkitMaskImage: 'linear-gradient(to bottom, #000 65%, transparent)' } : undefined}>
        <TextBody text={text} className={className} color={color} />
      </div>
      {long && (
        <button type="button" onClick={() => setAll(!all)} aria-expanded={all} aria-controls={id}
          className="mt-1 py-1 text-xs hover:underline focus-visible:outline focus-visible:outline-2 max-lg:min-h-10" style={{ color: 'var(--color-brand-light)' }}>
          {all ? 'Show less' : 'Show all'}
        </button>
      )}
    </div>
  );
}
